@if (auftrag$ | async; as auftrag) {
  @if (station$ | async; as station) {
    <soft-page>
      <soft-page-header>
        <div class="flex flex-col w-full gap-1">
          <div class="flex flex-row items-baseline justify-between">
            <h6 class="text-base font-bold">
              {{ station?.lieferadressen?.name1 ?? 'Kein Name' }}
              {{ station?.lieferadressen?.name2 }}
            </h6>
            @if (isCompleted$ | async) {
              <span
                class="bg-success-100 text-success-700 dark:bg-success-900 dark:text-success-300 text-[10px] font-semibold uppercase leading-none px-1.5 py-1 rounded whitespace-nowrap"
              >
                {{ '#TRANSPORT.ORDER_DETAIL.COMPLETED' | translate }}
              </span>
            }
          </div>
          <div class="flex flex-row items-center justify-between w-full">
            <span class="text-sm">
              {{ '#TRANSPORT.STATION_DETAIL.TITLE_PREFIX' | translate }}
              {{ station.lfdnr }}
            </span>
            <span class="text-sm">
              {{ '#TRANSPORT.STATION_DETAIL.ORDER_PREFIX' | translate }}
              {{ station?.belegnummer }}
            </span>
          </div>
        </div>
      </soft-page-header>

      <form [formGroup]="form">
        <!-- Customer -->
        <section class="mb-6 mt-4">
          <div class="flex flex-row items-end flex-nowrap justify-between mb-2">
            <h6 class="soft-overline">
              {{ '#TRANSPORT.STATION_DETAIL.CUSTOMER_OVERLINE' | translate }}
            </h6>
            <a
              class="soft-button secondary text-sm rounded"
              [href]="generateUrl($safeNavigationMigration(station?.lieferadressen))"
            >
              {{ '#TRANSPORT.STATION_DETAIL.MAPS_LINK' | translate }}
            </a>
          </div>
          @if (station?.lieferadressen; as address) {
            <div class="soft-card rounded-m p-3">
              <strong class="text-base font-semibold">
                {{ station?.lieferadressen?.name1 ?? 'Kein Name' }}
                {{ station?.lieferadressen?.name2 }}
              </strong>
              <div class="flex flex-col gap-1 mt-1">
                @if (address?.strasse && address.strasse.length > 0) {
                  <span class="text-sm">{{ address?.strasse }}</span>
                }
                @if (
                  (address?.postleitzahl && (address.postleitzahl.length ?? 0 > 0)) ||
                  (address?.ort && (address.ort.length ?? 0 > 0))
                ) {
                  <span class="text-sm">
                    {{ address?.postleitzahl }} {{ address?.ort }}
                  </span>
                }
                @if (isDefinedNotEmpty($safeNavigationMigration(address?.telefonnummern))) {
                  <div class="flex flex-col gap-1 mt-1 border-t pt-2">
                    @for (
                      telefon of address?.telefonnummern;
                      track telefon;
                      let i = $index
                    ) {
                      <div class="flex flex-row justify-between text-sm">
                        <span class="">{{ telefon.type }}</span>
                        <a class="soft-link" href="tel:{{ telefon.telefonnummer }}">
                          {{ telefon.telefonnummer }}
                        </a>
                      </div>
                    }
                  </div>
                } @else if (isDefinedNotEmpty($safeNavigationMigration(address?.telefonnummernList))) {
                  <div class="flex flex-col gap-1 mt-1 border-t pt-2">
                    @for (
                      telefon of address?.telefonnummernList;
                      track telefon;
                      let i = $index
                    ) {
                      <div class="flex flex-row justify-between text-sm">
                        <span class="">
                          {{ '#TRANSPORT.STATION_DETAIL.PHONE_PREFIX' | translate }}
                          {{ i + 1 }}
                        </span>
                        <a class="soft-link" href="tel:{{ telefon }}">{{ telefon }}</a>
                      </div>
                    }
                  </div>
                }
              </div>
            </div>
          }
        </section>

        <!-- Positionen -->
        <section class="mb-6">
          <h6 class="soft-overline mb-2">
            {{ '#TRANSPORT.STATION_DETAIL.POSITIONS_OVERLINE' | translate }}
          </h6>
          <div class="soft-card rounded-m p-3">
            @for (
              item of station.beweaus;
              track trackByFn(i, item);
              let i = $index;
              let last = $last
            ) {
              <div [class.border-b]="!last" [class.pb-2]="!last" [class.mb-2]="!last">
                <soft-tp-list-item-input
                  [item]="item"
                  [originalItem]="station._original.beweaus[i]"
                  [editable]="(isCompleted$ | async) !== true && item?.fixmenge !== true"
                  [index]="i"
                  (quantityChange)="quantityChanged(station, item, $event)"
                  (delete)="removeItem(station, item)"
                />
              </div>
            }
          </div>
          @if ((isCompleted$ | async) === false) {
            <div class="flex flex-row justify-end flex-nowrap mt-3">
              <button
                type="button"
                class="soft-button secondary rounded"
                (click)="onAddItem(station)"
              >
                <i class="fa-regular fa-plus mr-1"></i>
                {{ '#TRANSPORT.STATION_DETAIL.ADD_ITEM' | translate }}
              </button>
            </div>
          }
        </section>

        <!-- Additional info -->
        <section class="mb-6">
          <h6 class="soft-overline mb-2">
            {{ '#TRANSPORT.STATION_DETAIL.ADDITIONAL_INFO' | translate }}
          </h6>
          <div class="soft-card rounded-m p-3 space-y-4">
            <div class="flex flex-col">
              <span class="soft-label top mb-1">
                {{ '#TRANSPORT.STATION_DETAIL.BELEGVERSAND_OVERLINE' | translate }}
              </span>
              <soft-input
                [readonly]="true"
                formControlName="belegversandemail"
                placeholder="E-Mail"
              />
              @if (form.controls['belegversandemail'].touched) {
                <soft-validation-container
                  class="soft-property-validation"
                  [validation]="form | validationMessage: 'belegversandemail'"
                />
              }
            </div>
            <div class="flex flex-col">
              <span class="soft-label top mb-1">
                {{ '#TRANSPORT.STATION_DETAIL.EXT_LIEF_OVERLINE' | translate }}
              </span>
              <soft-number-input
                [readonly]="(isCompleted$ | async) ?? false"
                formControlName="extLfnr"
                placeholder="Ext. Liefernummer"
              />
            </div>
            <div class="flex flex-col">
              <span class="soft-label top mb-1">
                {{ '#TRANSPORT.STATION_DETAIL.DESCRIPTION_OVERLINE' | translate }}
              </span>
              <soft-multiline-input
                [readonly]="(isCompleted$ | async) ?? false"
                [lines]="3"
                [canResize]="true"
                formControlName="lieferhinweis"
                placeholder="Bemerkung / Anmerkung"
              />
            </div>
          </div>
        </section>

        @if ((isCompleted$ | async) === false) {
          <!-- Photos -->
          <section class="mb-6">
            <h6 class="soft-overline mb-2">
              {{ '#TRANSPORT.STATION_DETAIL.PHOTOS_OVERLINE' | translate }}
            </h6>
            <soft-file-input
              formControlName="files"
              selectionMode="multi"
              [sources]="'photo'"
            />
          </section>

          <!-- Signature -->
          <section class="mb-6">
            <h6 class="soft-overline mb-2">
              {{ '#TRANSPORT.STATION_DETAIL.SIGNATURE_OVERLINE' | translate }}
            </h6>
            <soft-signature-input formControlName="signature" />
          </section>
        }
      </form>
    </soft-page>
  }
}
